// covered-by: frontends/carrie/src/ask-v2/project/__tests__/AskV2OpenItemsSettled.test.tsx
import { type AskPunttSettledOpenItem } from "@mg/schemas/src/christo/askPunttOpenItems";
import { Heading } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { openItemRowTitle } from "./openItemRowTitle";

import { formatListCardRelativeDate } from "../../components/ask-puntt-list/listCardTimestamps";

const TITLE_ID = "ask-v2-open-items-settled-title";

function settledTitle(row: AskPunttSettledOpenItem): string {
  return openItemRowTitle({
    id: row.id,
    kind: row.kind,
    needed: row.needed,
    ownerUserId: null,
    openedAt: row.settledAt,
    fileId: null,
    sourceId: row.id,
    sourceKind: "agent",
  });
}

/**
 * Items the viewer raised in this project that an Expert settled in the last
 * 14 days: what they asked, how it was settled, by whom and when.
 */
export function AskV2OpenItemsSettled({
  settled,
}: {
  settled: AskPunttSettledOpenItem[];
}) {
  const { t } = useTranslation("ui");
  if (settled.length === 0) return null;
  return (
    <section
      aria-labelledby={TITLE_ID}
      data-testid="ask-v2-open-items-settled"
      className="mt-4 flex flex-col gap-1"
    >
      <Heading
        as="h3"
        id={TITLE_ID}
        size="2"
        weight="medium"
        className="text-puntt-neutral-gray-11"
      >
        {t("askV2.openItems.settledTitle")}
      </Heading>
      <ul className="flex flex-col">
        {settled.map((row) => {
          const when = formatListCardRelativeDate(
            row.settledAt instanceof Date
              ? row.settledAt.toISOString()
              : row.settledAt,
          );
          const by = row.settledByName
            ? t("askV2.openItems.settledBy", { name: row.settledByName })
            : t("askV2.openItems.settledByNobody");
          return (
            <li
              key={row.id}
              data-testid={`ask-v2-open-item-outcome-${row.id}`}
              aria-label={`${settledTitle(row)}. ${row.resolutionNote}`}
              className="flex list-none flex-col gap-1 border-t border-puntt-neutral-gray-6 py-2 text-[13px] text-puntt-neutral-gray-12 first:border-t-0"
            >
              <span className="block font-medium leading-snug">
                {settledTitle(row)}
              </span>
              <span className="block leading-snug">{row.resolutionNote}</span>
              <span className="block text-[12px] leading-snug text-puntt-neutral-gray-10">
                {when ? `${by} · ${when}` : by}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
